import {Dispatch, SetStateAction, useEffect, useRef, useState} from "react";
import type {Feature, FeatureCollection} from "geojson";
import {GeoJSON as LeafletGeoJSONType, PathOptions} from "leaflet";

import {useOpenStreetMapContext} from "../context/OpenStreetMapContext";

type IFeatureId = string | number;

export interface UseInteractiveGeoJsonOptions {
    data?: FeatureCollection;
    selectedFeaturesState?: [IFeatureId[], Dispatch<SetStateAction<IFeatureId[]>>];
    getFeatureId?: (feature: Feature) => IFeatureId;
    getFeatureLabel?: (feature: Feature, isSelected: boolean, isHovered: boolean) => string;
    getStyle?: (feature: Feature, isSelected: boolean) => PathOptions;
    shouldExtendBounds?: (feature: Feature) => boolean;
    onFeatureClick?: (feature: Feature, newSelectedState: boolean) => void;
    onFeatureMouseOver?: (feature: Feature) => void;
    onFeatureMouseOut?: (feature: Feature) => void;
    getHighlightStyle?: (feature: Feature, isSelected: boolean) => PathOptions;
}

export const useInteractiveGeoJson = (props: UseInteractiveGeoJsonOptions) => {
    const {
        data,
        getFeatureId = (feature) => (feature.id as string) || "",
        getFeatureLabel,
        shouldExtendBounds,
        getStyle = () => ({}),
        onFeatureClick,
        onFeatureMouseOver,
        onFeatureMouseOut,
        getHighlightStyle = () => ({})
    } = props;
    const localSelectedFeaturesState = useState<IFeatureId[]>([]);
    const [selectedFeatures, setSelectedFeatures] = props.selectedFeaturesState || localSelectedFeaturesState;
    const isMapDragging = useRef(false);
    const layerRef = useRef<LeafletGeoJSONType | null>(null);
    const {mapRef} = useOpenStreetMapContext();
    const lastLayersBBoxString = useRef("");
    const mapZoomLayerTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const mapZoomLabelTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const isFeatureSelected = (feature: Feature) => {
        return selectedFeatures.includes(getFeatureId(feature));
    };

    useEffect(() => {
        if (!mapRef || !data) {
            return;
        }

        if (layerRef.current) {
            mapRef.removeLayer(layerRef.current);
        }

        (async () => {
            const {geoJSON: LeafletGeoJson, LatLngBounds} = await import("leaflet");
            const newLayersBounds = new LatLngBounds([]);

            layerRef.current = LeafletGeoJson(data, {
                style: (feature) => {
                    if (!feature) {
                        return {
                            className: "leaflet-feature-layer"
                        };
                    }

                    return {...getStyle(feature, isFeatureSelected(feature)), className: "leaflet-feature-layer"};
                },
                onEachFeature: (feature, layer) => {
                    const refreshFeatureTooltip = (isHovered: boolean) => {
                        const featureLabel = getFeatureLabel?.(feature, isFeatureSelected(feature), isHovered);

                        if (featureLabel) {
                            layer.bindTooltip(featureLabel || "", {
                                permanent: true,
                                className: "leaflet-feature-layer-label",
                                direction: "center"
                            });
                        } else {
                            layer.unbindTooltip();
                        }
                    };

                    // set tooltips on mount
                    refreshFeatureTooltip(false);

                    if (!feature.properties.isStatic) {
                        layer.on("click", () => {
                            const id = getFeatureId(feature);
                            const isCurrentlySelected = selectedFeatures.includes(id);

                            const newSelectedState = !isCurrentlySelected;
                            const updatedSelection = newSelectedState ? [...selectedFeatures, id] : selectedFeatures.filter((sid) => sid !== id);

                            setSelectedFeatures(updatedSelection);

                            onFeatureClick?.(feature, newSelectedState);
                        });

                        layer.on("mouseover", (event) => {
                            if (isMapDragging.current) {
                                return;
                            }

                            onFeatureMouseOver?.(feature);
                            const targetLayer = event.target;

                            refreshFeatureTooltip(true);
                            targetLayer.setStyle(getHighlightStyle(feature, isFeatureSelected(feature)));
                            targetLayer.bringToFront();
                            targetLayer.getTooltip()?.bringToFront();
                        });

                        layer.on("mouseout", (e) => {
                            if (isMapDragging.current) {
                                return;
                            }

                            refreshFeatureTooltip(false);
                            onFeatureMouseOut?.(feature);

                            layerRef.current?.resetStyle(e.target);
                        });
                    }

                    if (shouldExtendBounds?.(feature)) {
                        newLayersBounds.extend(LeafletGeoJson(feature).getBounds());
                    }
                }
            });

            const layersBBoxString = newLayersBounds.isValid() ? newLayersBounds.toBBoxString() : "";
            if (lastLayersBBoxString.current !== layersBBoxString) {
                mapRef.fitBounds(newLayersBounds, {
                    animate: false
                });
                lastLayersBBoxString.current = layersBBoxString;
            }

            layerRef.current.addTo(mapRef);
        })();

        const onMapDragStart = () => {
            isMapDragging.current = true;
        };

        const onMapDragEnd = () => {
            isMapDragging.current = false;
        };

        const onMapZoomend = () => {
            const timeForTooltipDimensionUpdate = 100;
            const timeForTooltipUpdateAfterZoomOut = 300;

            mapZoomLayerTimeoutRef.current = setTimeout(() => {
                layerRef.current?.eachLayer((layer) => {
                    const tooltipElement = layer.getTooltip?.()?.getElement();
                    tooltipElement?.classList.add("leaflet-feature-layer-label-zoom-out");

                    layer.getTooltip?.()?.update();

                    mapZoomLabelTimeoutRef.current = setTimeout(() => {
                        tooltipElement?.classList.remove("leaflet-feature-layer-label-zoom-out");
                    }, timeForTooltipUpdateAfterZoomOut);
                });
            }, timeForTooltipDimensionUpdate);
        };

        mapRef.on("dragstart", onMapDragStart);
        mapRef.on("dragend", onMapDragEnd);
        mapRef.on("zoomend", onMapZoomend);

        return () => {
            clearTimeout(mapZoomLayerTimeoutRef.current);
            clearTimeout(mapZoomLabelTimeoutRef.current);

            if (layerRef.current) {
                layerRef.current.eachLayer((layer) => {
                    layer.off();
                    layer.unbindTooltip();
                    layer.removeFrom(mapRef);
                });

                layerRef.current.off().removeFrom(mapRef);
                layerRef.current = null;
            }

            mapRef.off("dragstart", onMapDragStart);
            mapRef.off("dragend", onMapDragEnd);
            mapRef.off("zoomend", onMapZoomend);
        };
    }, [mapRef, data, selectedFeatures, getFeatureId, getStyle, onFeatureClick, onFeatureMouseOver, onFeatureMouseOut, getHighlightStyle]);

    return {
        selectedFeatures: selectedFeatures,
        setSelectedFeatures: setSelectedFeatures
    };
};
import {Dispatch, SetStateAction, useEffect, useRef, useState} from "react";
import type {Feature, FeatureCollection} from "geojson";
import {GeoJSON as LeafletGeoJSONType, PathOptions} from "leaflet";

import {useOpenStreetMapContext} from "../context/OpenStreetMapContext";

type IFeatureId = string | number;

export interface UseInteractiveGeoJsonOptions {
    data?: FeatureCollection;
    selectedFeaturesState?: [IFeatureId[], Dispatch<SetStateAction<IFeatureId[]>>];
    getFeatureId?: (feature: Feature) => IFeatureId;
    getFeatureLabel?: (feature: Feature, isSelected: boolean, isHovered: boolean) => string;
    getStyle?: (feature: Feature, isSelected: boolean) => PathOptions;
    shouldExtendBounds?: (feature: Feature) => boolean;
    onFeatureClick?: (feature: Feature, newSelectedState: boolean) => void;
    onFeatureMouseOver?: (feature: Feature) => void;
    onFeatureMouseOut?: (feature: Feature) => void;
    getHighlightStyle?: (feature: Feature, isSelected: boolean) => PathOptions;
}

export const useInteractiveGeoJson = (props: UseInteractiveGeoJsonOptions) => {
    const {
        data,
        getFeatureId = (feature) => (feature.id as string) || "",
        getFeatureLabel,
        shouldExtendBounds,
        getStyle = () => ({}),
        onFeatureClick,
        onFeatureMouseOver,
        onFeatureMouseOut,
        getHighlightStyle = () => ({})
    } = props;
    const localSelectedFeaturesState = useState<IFeatureId[]>([]);
    const [selectedFeatures, setSelectedFeatures] = props.selectedFeaturesState || localSelectedFeaturesState;
    const isMapDragging = useRef(false);
    const layerRef = useRef<LeafletGeoJSONType | null>(null);
    const {mapRef} = useOpenStreetMapContext();
    const lastLayersBBoxString = useRef("");
    const mapZoomLayerTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const mapZoomLabelTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const isFeatureSelected = (feature: Feature) => {
        return selectedFeatures.includes(getFeatureId(feature));
    };

    useEffect(() => {
        if (!mapRef || !data) {
            return;
        }

        if (layerRef.current) {
            mapRef.removeLayer(layerRef.current);
        }

        (async () => {
            const {geoJSON: LeafletGeoJson, LatLngBounds} = await import("leaflet");
            const newLayersBounds = new LatLngBounds([]);

            layerRef.current = LeafletGeoJson(data, {
                style: (feature) => {
                    if (!feature) {
                        return {
                            className: "leaflet-feature-layer"
                        };
                    }

                    return {...getStyle(feature, isFeatureSelected(feature)), className: "leaflet-feature-layer"};
                },
                onEachFeature: (feature, layer) => {
                    const refreshFeatureTooltip = (isHovered: boolean) => {
                        const featureLabel = getFeatureLabel?.(feature, isFeatureSelected(feature), isHovered);

                        if (featureLabel) {
                            layer.bindTooltip(featureLabel || "", {
                                permanent: true,
                                className: "leaflet-feature-layer-label",
                                direction: "center"
                            });
                        } else {
                            layer.unbindTooltip();
                        }
                    };

                    // set tooltips on mount
                    refreshFeatureTooltip(false);

                    if (!feature.properties.isStatic) {
                        layer.on("click", () => {
                            const id = getFeatureId(feature);
                            const isCurrentlySelected = selectedFeatures.includes(id);

                            const newSelectedState = !isCurrentlySelected;
                            const updatedSelection = newSelectedState ? [...selectedFeatures, id] : selectedFeatures.filter((sid) => sid !== id);

                            setSelectedFeatures(updatedSelection);

                            onFeatureClick?.(feature, newSelectedState);
                        });

                        layer.on("mouseover", (event) => {
                            if (isMapDragging.current) {
                                return;
                            }

                            onFeatureMouseOver?.(feature);
                            const targetLayer = event.target;

                            refreshFeatureTooltip(true);
                            targetLayer.setStyle(getHighlightStyle(feature, isFeatureSelected(feature)));
                            targetLayer.bringToFront();
                            targetLayer.getTooltip()?.bringToFront();
                        });

                        layer.on("mouseout", (e) => {
                            if (isMapDragging.current) {
                                return;
                            }

                            refreshFeatureTooltip(false);
                            onFeatureMouseOut?.(feature);

                            layerRef.current?.resetStyle(e.target);
                        });
                    }

                    if (shouldExtendBounds?.(feature)) {
                        newLayersBounds.extend(LeafletGeoJson(feature).getBounds());
                    }
                }
            });

            const layersBBoxString = newLayersBounds.isValid() ? newLayersBounds.toBBoxString() : "";
            if (lastLayersBBoxString.current !== layersBBoxString) {
                mapRef.fitBounds(newLayersBounds, {
                    animate: false
                });
                lastLayersBBoxString.current = layersBBoxString;
            }

            layerRef.current.addTo(mapRef);
        })();

        const onMapDragStart = () => {
            isMapDragging.current = true;
        };

        const onMapDragEnd = () => {
            isMapDragging.current = false;
        };

        const onMapZoomend = () => {
            const timeForTooltipDimensionUpdate = 100;
            const timeForTooltipUpdateAfterZoomOut = 300;

            mapZoomLayerTimeoutRef.current = setTimeout(() => {
                layerRef.current?.eachLayer((layer) => {
                    const tooltipElement = layer.getTooltip?.()?.getElement();
                    tooltipElement?.classList.add("leaflet-feature-layer-label-zoom-out");

                    layer.getTooltip?.()?.update();

                    mapZoomLabelTimeoutRef.current = setTimeout(() => {
                        tooltipElement?.classList.remove("leaflet-feature-layer-label-zoom-out");
                    }, timeForTooltipUpdateAfterZoomOut);
                });
            }, timeForTooltipDimensionUpdate);
        };

        mapRef.on("dragstart", onMapDragStart);
        mapRef.on("dragend", onMapDragEnd);
        mapRef.on("zoomend", onMapZoomend);

        return () => {
            clearTimeout(mapZoomLayerTimeoutRef.current);
            clearTimeout(mapZoomLabelTimeoutRef.current);

            if (layerRef.current) {
                layerRef.current.eachLayer((layer) => {
                    layer.off();
                    layer.unbindTooltip();
                    layer.removeFrom(mapRef);
                });

                layerRef.current.off().removeFrom(mapRef);
                layerRef.current = null;
            }

            mapRef.off("dragstart", onMapDragStart);
            mapRef.off("dragend", onMapDragEnd);
            mapRef.off("zoomend", onMapZoomend);
        };
    }, [mapRef, data, selectedFeatures, getFeatureId, getStyle, onFeatureClick, onFeatureMouseOver, onFeatureMouseOut, getHighlightStyle]);

    return {
        selectedFeatures: selectedFeatures,
        setSelectedFeatures: setSelectedFeatures
    };
};
